import type {
  DashboardContractOverview as ContractOverviewResponse,
  DashboardContractUtilizationCard as ContractUtilizationCardData,
  DashboardProBonoUtilizationCard as ProBonoUtilizationCardData,
} from '@cartwheel/api/v1'
import { useDashboardContractOverviewQuery } from '@cartwheel/api/v1'
import { ErrorView } from '@cartwheel/components'
import ContractUtilizationCard from './contract-utilization-card'
import ContractUtilizationFeedbackBadge from './contract-utilization-feedback-badge'
import ProBonoUtilizationCard from './pro-bono-utilization-card'

interface ContractOverviewSectionProps {
  selectedContractId?: string
  selectedOrganizationId?: string
}

function ContractOverviewSkeleton() {
  return (
    <>
      <div className="flex flex-col gap-6 justify-center mx-auto px-5 lg:px-[4.5rem] pb-10 w-full">
        <div className="flex flex-wrap items-baseline justify-between gap-6">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl text-charcoal font-semibold">Contract Overview</h2>
          </div>
        </div>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
          {[0, 1].map((index) => (
            <div
              key={index}
              className="min-h-60 flex w-full *:h-5 *:w-full *:rounded *:bg-sand-50 flex-col justify-between rounded-[4px] border border-gray-stroke bg-white p-6 animate-pulse"
            >
              <div />
              <div />
              <div />
              <div />
            </div>
          ))}
        </div>
      </div>
      <div className="px-5 lg:px-[4.5rem] pb-10">
        <div className="h-px w-full bg-gray-stroke" />
      </div>
    </>
  )
}

function ContractOverviewContent({ response }: { response: ContractOverviewResponse }) {
  const contractUtilization = response.contract_utilization as
    | ContractUtilizationCardData
    | undefined
  const proBonoUtilization = response.pro_bono_utilization as ProBonoUtilizationCardData | undefined
  const bothCardsPresent = !!contractUtilization && !!proBonoUtilization
  const cardClass = bothCardsPresent ? 'w-full lg:flex-1' : 'w-full'

  if (!contractUtilization && !proBonoUtilization) {
    return null
  }

  return (
    <>
      <div className="flex flex-col gap-6 justify-center mx-auto px-5 lg:px-[4.5rem] pb-10 w-full">
        <div className="flex flex-wrap items-baseline justify-between gap-6">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl text-charcoal font-semibold">Contract Overview</h2>
            {response.feedback_state ? (
              <div className="ml-4">
                <ContractUtilizationFeedbackBadge feedback_state={response.feedback_state} />
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch">
          {contractUtilization ? (
            <div className={cardClass}>
              <ContractUtilizationCard {...contractUtilization} />
            </div>
          ) : null}
          {proBonoUtilization ? (
            <div className={cardClass}>
              <ProBonoUtilizationCard {...proBonoUtilization} />
            </div>
          ) : null}
        </div>
      </div>
      <div className="px-5 lg:px-[4.5rem] pb-10">
        <div className="h-px w-full bg-gray-stroke" />
      </div>
    </>
  )
}

export default function ContractOverviewSection({
  selectedContractId,
  selectedOrganizationId,
}: ContractOverviewSectionProps) {
  const { data, error, isLoading } = useDashboardContractOverviewQuery({
    query: {
      contract_id: selectedContractId,
      organization_id: selectedOrganizationId,
    },
  })

  if (isLoading) {
    return <ContractOverviewSkeleton />
  }

  if (error) {
    return (
      <div className="px-5 lg:px-[4.5rem] pb-10">
        <ErrorView
          title="Error loading contract overview"
          message={error.message || 'Unknown error'}
        />
      </div>
    )
  }

  if (!data) {
    return null
  }

  return <ContractOverviewContent response={data} />
}
